/**
 * Joining a business from an invitation (plan §F.5.6, step F3).
 *
 * An admin invites a person from Settings' People and sends them the link,
 * `/join?code=…`, by their own means. This page reads the code, says which
 * business it is for and in what role, and asks for what signing in needs:
 * the password of a sign-in the address already has, or a new password and an
 * authenticator. Signup's rules hold, and its header argues them:
 *
 *   - **nothing is written until the last step**, and the screen says so;
 *   - the address is shown, never asked for: it is the invitation's;
 *   - a new sign-in ends on its recovery codes, shown once.
 *
 * Public, outside the shell, as Login and Signup are: the person has no
 * session in this business yet.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { ApiError, api, type BegunJoin, type JoinKey } from '../api.js';
import { RecoveryCodes } from '../components/RecoveryCodes.js';
import { reloadTo } from '../lib/reload.js';

type Step = 'key' | 'password' | 'code' | 'codes';

const ROLE_WORDS: Record<JoinKey['role'], string> = {
  admin: 'an admin',
  bookkeeper: 'a bookkeeper',
  viewer: 'a viewer',
};

function messageFor(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    if (error.code === 'network_unreachable') return 'Cannot reach the server. Check your connection and try again.';
    return error.message === error.code ? fallback : error.message;
  }
  return fallback;
}

export function JoinPage() {
  const [step, setStep] = useState<Step>('key');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState(() => new URLSearchParams(window.location.search).get('code') ?? '');
  const [invited, setInvited] = useState<JoinKey | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [enrolment, setEnrolment] = useState<BegunJoin | null>(null);
  const [totp, setTotp] = useState('');
  const [issued, setIssued] = useState<string[] | null>(null);

  async function run(work: () => Promise<void>, fallback: string) {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (caught) {
      setError(messageFor(caught, fallback));
    } finally {
      setBusy(false);
    }
  }

  async function checkKey(value: string) {
    await run(async () => {
      setInvited(await api.joinKey(value.trim()));
      setStep('password');
    }, 'That invitation is not valid.');
  }

  // The link carries the code, so the first step usually answers itself.
  useEffect(() => {
    if (code.trim() !== '') void checkKey(code);
    // Once, on arrival: a code typed later is submitted with the button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitKey = (event: FormEvent) => {
    event.preventDefault();
    void checkKey(code);
  };

  const submitPassword = (event: FormEvent) => {
    event.preventDefault();
    if (invited && !invited.existingLogin && password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }
    void run(async () => {
      setEnrolment(await api.joinBegin(code.trim(), password));
      setStep('code');
    }, 'Could not check that password.');
  };

  const submitCode = (event: FormEvent) => {
    event.preventDefault();
    if (!enrolment) return;
    void run(async () => {
      const joined = await api.joinComplete(enrolment.token, totp.trim());
      setPassword('');
      setConfirm('');
      // A sign-in that already existed keeps its own recovery codes.
      if (joined.codes.length === 0) {
        reloadTo('/');
        return;
      }
      setIssued(joined.codes);
      setStep('codes');
    }, 'Could not finish joining.');
  };

  return (
    <main className="centered">
      <h1>Techne Books</h1>

      {step === 'key' && (
        <form onSubmit={submitKey}>
          <p className="muted">Enter the invitation code you were sent.</p>
          <label htmlFor="join-code">Invitation Code</label>
          <input
            id="join-code"
            autoComplete="off"
            required
            value={code}
            onChange={(event) => setCode(event.target.value)}
            disabled={busy}
          />
          {error && <p role="alert" className="error">{error}</p>}
          <button type="submit" disabled={busy}>
            {busy ? 'Checking…' : 'Continue'}
          </button>
        </form>
      )}

      {step === 'password' && invited && (
        <form onSubmit={submitPassword}>
          <p>
            Joining <strong>{invited.businessName}</strong> as {ROLE_WORDS[invited.role]}, as{' '}
            <strong>{invited.email}</strong>.
          </p>
          {invited.existingLogin ? (
            <p className="muted">
              This address already signs in. Enter that password here, and the next step asks for a code from the
              authenticator you sign in with.
            </p>
          ) : (
            <p className="muted">Choose a password; the next step adds an authenticator.</p>
          )}
          <p className="muted">Nothing is saved until the last step.</p>

          <label htmlFor="join-password">{invited.existingLogin ? 'Your Password' : 'Password'}</label>
          <input
            id="join-password"
            type="password"
            autoComplete={invited.existingLogin ? 'current-password' : 'new-password'}
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            disabled={busy}
          />
          {!invited.existingLogin && (
            <>
              <label htmlFor="join-confirm">Confirm Password</label>
              <input
                id="join-confirm"
                type="password"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                disabled={busy}
              />
            </>
          )}
          {error && <p role="alert" className="error">{error}</p>}
          <button type="submit" disabled={busy}>
            {busy ? 'Checking…' : 'Continue'}
          </button>
        </form>
      )}

      {step === 'code' && enrolment && (
        <form onSubmit={submitCode}>
          {enrolment.existingLogin ? (
            <p className="muted">Enter the code from the authenticator you sign in with as {invited?.email}.</p>
          ) : (
            <>
              <p className="muted">
                Add this account to an authenticator app, then enter the code it shows. Signing in needs it every
                time, so put it somewhere you will keep.
              </p>
              <p className="muted">
                On this device, open <a href={enrolment.otpauthUri ?? undefined}>the authenticator link</a>. On
                another, add it by hand with this key:
              </p>
              <p><code className="enrolment-secret">{enrolment.secret}</code></p>
            </>
          )}
          <p className="muted">
            <strong>Nothing has been saved yet.</strong> You join the business when this code is accepted, and your
            invitation is still good until then.
          </p>
          <label htmlFor="join-totp">Six-digit code</label>
          <input
            id="join-totp"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            required
            value={totp}
            onChange={(event) => setTotp(event.target.value)}
            disabled={busy}
          />
          {error && <p role="alert" className="error">{error}</p>}
          <button type="submit" disabled={busy}>
            {busy ? 'Joining…' : 'Join The Business'}
          </button>
        </form>
      )}

      {step === 'codes' && issued && (
        <RecoveryCodes
          codes={issued}
          reason="joined"
          // A full reload: the session cookie was set by the request that
          // joined, and the shell reads the session once on mount.
          onDone={() => reloadTo('/')}
        />
      )}

      <p className="muted">
        <a href="/privacy">Privacy Policy</a>
      </p>
    </main>
  );
}
